/**
 * CFO Dashboard — executive financial overview.
 *
 * Layout: KPI row -> Trend + Waterfall -> Donut + Bar -> Data Table
 * All data from Semantic Layer API. Click any KPI for drill-down.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { TrendChart, BarChart, WaterfallChart, DonutChart, DataTable } from '../components/charts';
import { CHART_COLORS, formatCurrency, formatMetricValue } from '../config/chartTheme';
import KpiCardUI from '../components/ui/KpiCard';
import PageLayout from '../components/ui/PageLayout';
import PeriodSelector from '../components/PeriodSelector';
import EntitySelector from '../components/EntitySelector';
import { BASE, authFetch } from '../constants';
import { useGlobalEntity } from '../hooks/useGlobalEntity';

// --- Types ---

interface MetricResult {
  metric_code: string;
  metric_name: string;
  numeric_value: number | null;
  formatted: string;
  unit: string;
  category: string;
  period_code: string;
  period_name: string;
  display_order: number;
  benchmark_good?: number | null;
  benchmark_warning?: number | null;
  benchmark_critical?: number | null;
}

interface TrendPoint {
  period: string;
  value: number;
  period_code: string;
}

interface DrillState {
  metric_code: string;
  metric_name: string;
  unit: string;
}

// --- Top-level KPI codes (order matters for display) ---

const TOP_KPIS = [
  'revenue', 'net_income', 'gross_margin',
  'working_capital', 'dso', 'exception_count',
];

const KPI_COLORS: Record<string, string> = {
  revenue: 'var(--navy-800)',
  net_income: 'var(--positive)',
  gross_margin: 'var(--navy-600)',
  working_capital: 'var(--navy-800)',
  dso: 'var(--warning)',
  exception_count: 'var(--negative)',
};

// --- Styles ---

const sectionHeader: React.CSSProperties = {
  fontSize: '11px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  fontWeight: 500,
  color: 'var(--text-muted)',
  marginBottom: '14px',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
};

const dot: React.CSSProperties = {
  width: 6, height: 6, borderRadius: '50%',
  background: 'var(--navy-800)',
  display: 'inline-block',
};

const cardContainer: React.CSSProperties = {
  background: 'var(--bg-card)',
  border: '1px solid var(--border-default)',
  borderRadius: '14px',
  padding: '20px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
};

// --- Component ---

export default function CfoDashboard() {
  const { entity: globalEntity } = useGlobalEntity();
  const [periodCode, setPeriodCode] = useState('');
  const [entityCode, setEntityCode] = useState(globalEntity || '*');
  useEffect(() => { setEntityCode(globalEntity || '*'); }, [globalEntity]);
  const [metrics, setMetrics] = useState<MetricResult[]>([]);
  const [trendData, setTrendData] = useState<TrendPoint[]>([]);
  const [drillMetric, setDrillMetric] = useState<DrillState | null>(null);
  const [drillTrend, setDrillTrend] = useState<TrendPoint[]>([]);
  const [loading, setLoading] = useState(true);

  // --- Fetch dashboard metrics ---

  const fetchDashboard = useCallback(async () => {
    if (!periodCode) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams({
        entity_code: entityCode,
        period_code: periodCode,
      }).toString();
      const res = await authFetch(`${BASE}/api/v1/metrics/financial/dashboard?${qs}`);
      const data = await res.json();
      setMetrics(data.metrics || []);
    } catch {
      setMetrics([]);
    }
    setLoading(false);
  }, [periodCode, entityCode]);

  // --- Fetch revenue trend ---

  const fetchRevenueTrend = useCallback(async () => {
    try {
      const qs = new URLSearchParams({
        entity_code: entityCode,
        periods: '12',
      }).toString();
      const res = await authFetch(`${BASE}/api/v1/metrics/financial/trend/revenue?${qs}`);
      const data = await res.json();
      const trend = (data.trend || []).map((t: any) => ({
        period: t.period_name || t.period_code,
        value: t.numeric_value ?? 0,
        period_code: t.period_code,
      }));
      setTrendData(trend);
    } catch {
      setTrendData([]);
    }
  }, [entityCode]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);
  useEffect(() => { fetchRevenueTrend(); }, [fetchRevenueTrend]);

  // --- Drill-down: fetch trend for clicked metric ---

  const handleKpiClick = useCallback(async (m: MetricResult) => {
    if (drillMetric?.metric_code === m.metric_code) {
      setDrillMetric(null);
      setDrillTrend([]);
      return;
    }
    setDrillMetric({ metric_code: m.metric_code, metric_name: m.metric_name, unit: m.unit });
    try {
      const qs = new URLSearchParams({ entity_code: entityCode, periods: '12' }).toString();
      const res = await authFetch(
        `${BASE}/api/v1/metrics/financial/trend/${m.metric_code}?${qs}`
      );
      const data = await res.json();
      setDrillTrend((data.trend || []).map((t: any) => ({
        period: t.period_name || t.period_code,
        value: t.numeric_value ?? 0,
        period_code: t.period_code,
      })));
    } catch {
      setDrillTrend([]);
    }
  }, [entityCode, drillMetric]);

  // --- Helpers ---

  const getMetric = (code: string): MetricResult | undefined =>
    metrics.find(m => m.metric_code === code);

  const getBenchmark = (m: MetricResult): 'good' | 'warning' | 'critical' | null => {
    const v = m.numeric_value;
    if (v == null) return null;
    if (m.benchmark_good != null && v >= m.benchmark_good) return 'good';
    if (m.benchmark_critical != null && v <= m.benchmark_critical) return 'critical';
    if (m.benchmark_warning != null && v <= m.benchmark_warning) return 'warning';
    return null;
  };

  // Build expense breakdown for donut
  const expenseMetrics = metrics.filter(m =>
    ['cogs', 'operating_expenses', 'total_expenses', 'sga_expenses'].includes(m.metric_code)
  );

  const donutData = expenseMetrics
    .filter(m => m.numeric_value && m.numeric_value > 0)
    .map((m, i) => ({
      label: m.metric_name,
      value: Math.abs(m.numeric_value || 0),
      color: CHART_COLORS.series[i % CHART_COLORS.series.length],
    }));

  // Build department exception bar (from operational metrics)
  const deptMetrics = metrics.filter(m => m.category === 'OPERATIONAL' && m.numeric_value);

  // Build waterfall: Revenue -> COGS -> Gross Profit -> OpEx -> Net Income
  const rev = getMetric('revenue')?.numeric_value || 0;
  const cogs = getMetric('cogs')?.numeric_value || 0;
  const gp = getMetric('gross_profit')?.numeric_value || 0;
  const opex = getMetric('operating_expenses')?.numeric_value || 0;
  const ni = getMetric('net_income')?.numeric_value || 0;

  const waterfallData = [
    { label: 'Revenue', value: rev, type: 'total' as const },
    { label: 'COGS', value: -Math.abs(cogs), type: 'decrease' as const },
    { label: 'Gross Profit', value: gp, type: 'total' as const },
    { label: 'OpEx', value: -Math.abs(opex), type: 'decrease' as const },
    { label: 'Net Income', value: ni, type: 'total' as const },
  ];

  // Account-level data table from income statement metrics
  const tableData = metrics
    .filter(m => m.category === 'INCOME_STATEMENT' || m.category === 'BALANCE_SHEET')
    .map(m => ({
      metric_code: m.metric_code,
      metric_name: m.metric_name,
      category: m.category,
      value: m.numeric_value,
      formatted: m.formatted,
      unit: m.unit,
    }));

  // --- Render ---

  return (
    <PageLayout title="CFO Dashboard" subtitle="Executive financial overview">
      {/* Selectors */}
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: 20, justifyContent: 'flex-end', marginTop: -40 }}>
        <EntitySelector value={entityCode} onChange={setEntityCode} />
        <PeriodSelector value={periodCode} onChange={setPeriodCode} />
      </div>

      {/* KPI Row */}
      <div style={sectionHeader}><span style={dot} /> Key Metrics</div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '12px',
        marginBottom: '24px',
      }}>
        {TOP_KPIS.map((code, i) => {
          const m = getMetric(code);
          const val = m ? formatMetricValue(m.numeric_value, m.unit) : '\u2014';
          return (
            <KpiCardUI
              key={code}
              label={m?.metric_name || code.replace(/_/g, ' ')}
              value={loading ? '...' : val}
              valueColor={KPI_COLORS[code] || 'var(--text-primary)'}
              onClick={m ? () => handleKpiClick(m) : undefined}
            />
          );
        })}
      </div>

      {/* Drill-down panel (expands when KPI clicked) */}
      {drillMetric && (
        <div style={{
          ...cardContainer,
          marginBottom: '24px',
          animation: 'fadeIn 0.3s ease',
        }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: '12px',
          }}>
            <div style={sectionHeader}>
              <span style={dot} /> {drillMetric.metric_name} -- 12-Period Trend
            </div>
            <button
              onClick={() => { setDrillMetric(null); setDrillTrend([]); }}
              style={{
                background: 'transparent', border: 'none',
                color: 'var(--text-muted, #64748B)', cursor: 'pointer',
                fontSize: '18px', padding: '0 4px',
              }}
            >
              &times;
            </button>
          </div>
          {drillTrend.length > 0 ? (
            <TrendChart
              data={drillTrend}
              unit={drillMetric.unit}
              height={280}
              showArea
              color={CHART_COLORS.accent}
            />
          ) : (
            <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '40px' }}>
              No trend data available
            </div>
          )}
        </div>
      )}

      {/* Row 2: Revenue Trend + P&L Bridge */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '16px',
        marginBottom: '24px',
      }}>
        {/* Revenue Trend */}
        <div style={cardContainer}>
          <div style={sectionHeader}><span style={dot} /> Revenue Trend</div>
          {trendData.length > 0 ? (
            <TrendChart
              data={trendData}
              unit="USD"
              height={250}
              showArea
              color={CHART_COLORS.accent}
            />
          ) : (
            <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '60px 0' }}>
              {loading ? 'Loading...' : 'No revenue data for selected period'}
            </div>
          )}
        </div>

        {/* P&L Bridge */}
        <div style={cardContainer}>
          <div style={sectionHeader}><span style={dot} /> P&L Bridge</div>
          {rev > 0 ? (
            <WaterfallChart data={waterfallData} unit="USD" height={250} />
          ) : (
            <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '60px 0' }}>
              {loading ? 'Loading...' : 'No P&L data for selected period'}
            </div>
          )}
        </div>
      </div>

      {/* Row 3: Expense Breakdown + Exception by Department */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
        gap: '16px',
        marginBottom: '24px',
      }}>
        {/* Expense Breakdown */}
        <div style={cardContainer}>
          <div style={sectionHeader}><span style={dot} /> Expense Composition</div>
          {donutData.length > 0 ? (
            <DonutChart
              data={donutData}
              height={220}
              unit="USD"
              centerLabel={formatCurrency(
                donutData.reduce((s, d) => s + d.value, 0)
              )}
              centerSubLabel="Total"
            />
          ) : (
            <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '60px 0' }}>
              {loading ? 'Loading...' : 'No expense data'}
            </div>
          )}
        </div>

        {/* Operational metrics bar */}
        <div style={cardContainer}>
          <div style={sectionHeader}><span style={dot} /> Operational Metrics</div>
          {deptMetrics.length > 0 ? (
            <BarChart
              data={deptMetrics.map(m => ({
                label: m.metric_name.replace(/_/g, ' '),
                value: m.numeric_value || 0,
              }))}
              height={220}
              unit="COUNT"
              horizontal
              color={CHART_COLORS.series[1]}
            />
          ) : (
            <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '60px 0' }}>
              {loading ? 'Loading...' : 'No operational data'}
            </div>
          )}
        </div>
      </div>

      {/* Row 4: Metrics Detail Table */}
      <div style={cardContainer}>
        <div style={sectionHeader}><span style={dot} /> Financial Metrics Detail</div>
        {tableData.length > 0 ? (
          <DataTable
            columns={[
              { field: 'metric_name', headerName: 'Metric', flex: 2 },
              { field: 'category', headerName: 'Category', width: 160 },
              { field: 'formatted', headerName: 'Value', width: 140, cellClass: 'ag-cell-mono' },
              { field: 'unit', headerName: 'Unit', width: 80 },
            ]}
            data={tableData}
            height={Math.min(400, 56 + tableData.length * 42)}
          />
        ) : (
          <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '40px 0' }}>
            {loading ? 'Loading metrics...' : 'No metrics available. Upload GL data to populate.'}
          </div>
        )}
      </div>
    </PageLayout>
  );
}
